/**
 * CCReferenceDocs Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive reference document browser for Council Corner with advanced features:
 * - Dual view modes: Card view for detailed browsing, List view for quick scanning
 * - Advanced search functionality with real-time filtering
 * - Pagination system with smooth loading states
 * - Interactive PDF modal integration with multi-window support
 * - Search term highlighting in results
 * - Applied filter management and reset functionality
 * - Professional statistics display with document counts
 * - Responsive design with IFIA brand styling
 * - Production-ready with comprehensive error handling
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of reference document objects with id, title, summary, and fileName
 * @returns {JSX.Element} Reference documents browser component
 */
import React, { useState, useMemo } from 'react';
import PDFModal from '../../modals/PDFModal';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css'; 

import './CCReferenceDocs.css';

/**
 * CCReferenceDocs Component
 * 
 * Council Corner reference documents browser with search, filtering, and PDF modal integration.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Reference document data array
 * @returns {JSX.Element} Reference documents component
 */
function CCReferenceDocs({ data }) {
  const [modals, setModals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTabIndex, setActiveTabIndex] = useState(0); // Track active tab
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [loadingPageNumber, setLoadingPageNumber] = useState(1);
  const itemsPerPage = 6; // Number of items to show per page

  /**
   * Cleans text content by removing formatting characters and normalizing whitespace
   * 
   * @param {string} text - Raw text content to clean
   * @returns {string} Cleaned text suitable for display and search
   */
  const cleanText = (text) => {
    return text
      .replace(/\*\*/g, '')
      .replace(/""/g, '')
      .replace(/''/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Filter and paginate data
  const filteredData = useMemo(() => {
    return data.filter(doc => 
      cleanText(doc.title).toLowerCase().includes(searchQuery.toLowerCase()) ||
      cleanText(doc.summary).toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [data, searchQuery]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage]);

  /**
   * Opens PDF modal with proper stacking and positioning
   * 
   * Handles multiple modal windows with z-index management and prevents duplicates.
   * 
   * @param {string} id - Unique identifier for the document
   * @param {string} title - Document title for modal header
   * @param {string} pdfUrl - URL or filename of the PDF document
   */
  const openPDFModal = (id, title, pdfUrl) => {
    const existingModalIndex = modals.findIndex((modal) => modal.id === id);
    const maxZIndex = Math.max(...modals.map((modal) => modal.zIndex), 0) + 1;

    // // Use ID-based URL instead of filename
    // const safeUrl = `${process.env.REACT_APP_API_URL}graph/getFileContentByListItemId/${id}`;

    if (existingModalIndex !== -1) {
      const updatedModals = [...modals];
      updatedModals[existingModalIndex].isOpen = true;
      updatedModals[existingModalIndex].zIndex = maxZIndex;
      setModals(updatedModals);
    } else {
      const newModal = {
        id,
        title,
        pdfUrl: pdfUrl, 
        isOpen: true,
        top: `${15 + (modals.length * 2)}%`,
        left: `${30 + (modals.length * 2)}%`,
        zIndex: maxZIndex,
      };
      setModals((prevModals) => [...prevModals, newModal]);
    }
  };

  const closePDFModal = (id) => {
    setModals((prevModals) =>
      prevModals.map((modal) =>
        modal.id === id ? { ...modal, isOpen: false } : modal
      )
    );
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setLoadingPageNumber(newPage);
      setCurrentPage(newPage);
      setIsLoadingPage(true);
      
      // Scroll to top of content
      document.querySelector('.cc-reference-docs')?.scrollIntoView({ behavior: 'smooth' });
      
      // Simulate loading delay
      setTimeout(() => {
        setIsLoadingPage(false);
      }, 300);
    }
  };

  // Handle tab change
  const handleTabChange = (index) => {
    setActiveTabIndex(index);
    setCurrentPage(1); // Reset to first page when changing views
  };

  // Reset filters function
  const resetFilters = () => {
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Check if any filters are active
  const isFilterActive = () => {
    return searchQuery !== '';
  };

  /**
   * Highlights search terms in text content
   * 
   * @param {string} text - Text content to highlight
   * @param {string} searchTerm - Search term to highlight
   * @returns {string} HTML string with highlighted search terms
   */
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm.trim()) return text;
    
    const regex = new RegExp(`(${searchTerm.trim()})`, 'gi');
    return text.replace(regex, `<span class="highlight-match">$1</span>`);
  };

  return (
    <div className="cc-reference-docs">
      <Tabs selectedIndex={activeTabIndex} onSelect={handleTabChange}>


        {/* Stats Panel & View Controls */}
        <div className="cc-filters-status">
          <div className="applied-filters-section">
            {searchQuery && (
              <div className="applied-filters">
                <span className="applied-filters-label">Applied filters:</span>
                <div className="filter-tags">
                  <div className="filter-tag">
                    <span className="filter-tag-label">Search:</span>
                    <span className="filter-tag-value">{searchQuery}</span>
                    <button 
                      className="filter-tag-remove"
                      onClick={() => {
                        setSearchQuery('');
                        setCurrentPage(1);
                      }}
                      title="Remove search filter"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="cc-search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={handleSearch}
            />
            {searchQuery && (
              <i 
                className="fas fa-times clear-search" 
                onClick={() => {
                  setSearchQuery('');
                  setCurrentPage(1);
                }}
              />
            )}
          </div>
          
          <div className="instant-stats-panel">
            <div className="stats-info">
              <div className="stats-description">
                <i className="fa-solid fa-chart-simple"></i>
                {filteredData.length === 1 ? '1 document' : `${filteredData.length} documents`} found
              </div>
              {filteredData.length < data.length && (
                <div className="stats-breakdown">
                  <i className="fas fa-filter"></i>
                  Filtered from {data.length.toLocaleString()} total
                </div>
              )}
            </div>
            
            <div className="view-controls">
              <button 
                className={`view-btn ${activeTabIndex === 0 ? 'active' : ''}`}
                onClick={() => handleTabChange(0)}
                title="Card View"
              >
                <i className="fas fa-th"></i>
              </button>
              <button 
                className={`view-btn ${activeTabIndex === 1 ? 'active' : ''}`}
                onClick={() => handleTabChange(1)}
                title="List View"
              >
                <i className="fas fa-list"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Hidden TabList for react-tabs functionality */}
        <TabList style={{ display: 'none' }}>
          <Tab>Card View</Tab>
          <Tab>List View</Tab>
        </TabList>

        <TabPanel>
          {isLoadingPage && (
            <div className="page-loading">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Loading page {loadingPageNumber}...</span>
            </div>
          )}
          {paginatedData.length === 0 ? (
            <div className="no-results">
              <div className="no-results-content">
                <i className="fas fa-search"></i>
                <h3>No documents found</h3>
                <p>Try adjusting your search criteria to see more results.</p>
              </div>
            </div>
          ) : (
            <section className="minutes-grid">
              {paginatedData.map((doc) => (
                <Tippy 
                  key={doc.id}
                  content={doc.summary}
                  placement="right"
                  interactive={true}
                  appendTo={() => document.body}
                  theme="light"
                  duration={200}
                  delay={[200, 0]}
                >
                  <div 
                    className="minute-card"
                    onClick={(e) => {
                      e.stopPropagation();
                      openPDFModal(doc.id, doc.title, doc.fileName);
                    }}
                  >
                    <div className="minute-card-content">
                      <h3 className="minute-title">
                        {searchQuery.trim() ? (
                          <span dangerouslySetInnerHTML={{ 
                            __html: highlightSearchTerm(cleanText(doc.title), searchQuery)
                          }} />
                        ) : (
                          cleanText(doc.title)
                        )}
                      </h3>
                      <p className="minute-summary">
                        {searchQuery.trim() ? (
                          <span dangerouslySetInnerHTML={{ 
                            __html: highlightSearchTerm(cleanText(doc.summary), searchQuery)
                          }} />
                        ) : (
                          cleanText(doc.summary)
                        )}
                      </p>
                      <div className="card-footer">
                        <div className="pdf-icon">
                          <i className="fa-regular fa-file-pdf"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </Tippy>
              ))}
            </section>
          )}
        </TabPanel>

        <TabPanel>
          {isLoadingPage && (
            <div className="page-loading">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Loading page {loadingPageNumber}...</span>
            </div>
          )}
          {paginatedData.length === 0 ? (
            <div className="no-results">
              <div className="no-results-content">
                <i className="fas fa-search"></i>
                <h3>No documents found</h3>
                <p>Try adjusting your search criteria to see more results.</p>
              </div>
            </div>
          ) : (
            <div className="minutes-list">
              {paginatedData.map((doc) => (
                <div 
                  key={doc.id} 
                  className="meeting-list-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    openPDFModal(doc.id, doc.title, doc.fileName);
                  }}
                >
                  <div className="meeting-list-content">
                    <div className="meeting-list-header">
                      <h3 className="meeting-list-title">
                        {searchQuery.trim() ? (
                          <span dangerouslySetInnerHTML={{ 
                            __html: highlightSearchTerm(cleanText(doc.title), searchQuery)
                          }} />
                        ) : (
                          cleanText(doc.title)
                        )}
                      </h3>
                    </div>
                    <p className="meeting-list-summary">
                      {searchQuery.trim() ? (
                        <span dangerouslySetInnerHTML={{ 
                          __html: highlightSearchTerm(cleanText(doc.summary), searchQuery)
                        }} />
                      ) : (
                        cleanText(doc.summary)
                      )}
                    </p>
                    <div className="list-footer">
                      <div className="pdf-icon">
                        <i className="fa-regular fa-file-pdf"></i>
                   
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabPanel>

        {/* Pagination Controls - ResearchCentre style */}
        {totalPages > 1 && !isLoadingPage && (
          <div className="pagination">
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
            </div>
            <div className="pagination-controls">
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                <i className="fas fa-angle-double-left"></i>
              </button>
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <i className="fas fa-angle-left"></i>
              </button>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    className={`pagination-btn ${pageNum === currentPage ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <i className="fas fa-angle-right"></i>
              </button>
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                <i className="fas fa-angle-double-right"></i>
              </button>
            </div>
          </div>
        )}
      </Tabs>

      {modals.map((modal) => (
        <PDFModal
          key={modal.id}
          title={modal.title}
          url={modal.pdfUrl}
          show={modal.isOpen}
          onClose={(e) => {
            e.stopPropagation();
            closePDFModal(modal.id);
          }}
          allowFullscreen={true}
          allowMove={true}
          showLinkinToolBar={true}
        
        />
      ))}
    </div>
  );
}

export default CCReferenceDocs;